import * as React from 'react';
import { useTranslation } from '@mukil/i18n';
import {
  AdaptiveDialog,
  Alert,
  Button,
  DatePicker,
  FormField,
  StyledSelect,
  Textarea,
  toast,
} from '@jumpstone/ui-react';
import type { RealEmployeeRecord } from '../../data/wagepro_store_employees';
import {
  withPayCurrencyFallbacks,
  type PayCurrencyOption,
} from '../../data/use_pay_currencies';
import type {
  AffectedPayRun,
  CompensationMutationFailure,
  CompensationPayBasis,
  CompensationReason,
  ScheduleCompensationInput,
  ScheduleCompensationResult,
} from '../../data/use_employee_compensation';
import { format_date } from '../../lib/format';
import { COMPENSATION_REASON_KEY } from '../../lib/label_keys';
import { useDatePickerLabels } from '../../lib/use_date_picker_labels';
import { EmployeePayAmountField } from './employee_pay_amount_field';
import { CompensationFailureAlert } from './compensation_failure_alert';
import {
  COMPENSATION_REASONS,
  todayIsoUtc,
} from './employee_form_compensation_utils';
import {
  NOTE_MAX_LENGTH,
  amountForBasis,
  buildScheduleInput,
  currentFromRecord,
  initialChangeSalaryDraft,
  isCurrencyChanged,
  validateChangeSalary,
  type ChangeSalaryAmountError,
  type ChangeSalaryDateError,
  type ChangeSalaryDraft,
  type ChangeSalaryErrors,
} from './change_salary_validation';
import {
  formatBackPayPeriods,
  isSameDateConflict,
} from './change_salary_utils';

const SELECT_TRIGGER_CLASSES =
  'nv-input flex h-9 w-full cursor-pointer items-center justify-between gap-2';

const DATE_ERROR_KEY: Record<ChangeSalaryDateError, string> = {
  required: 'employees.form_error_required',
  before_start: 'employees.form_error_effective_from_before_start',
  after_termination: 'employees.change_salary_error_after_termination',
};

const AMOUNT_ERROR_KEY: Record<ChangeSalaryAmountError, string> = {
  salary_required: 'employees.form_error_salary_required',
  hourly_rate_required: 'employees.compensation_error_hourly_rate_required',
};

export interface ChangeSalaryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The employee whose pay is being changed (raw record: the dialog reads the pay facts and date bounds from it). */
  employee: RealEmployeeRecord;
  /** The organisation's base currency code. */
  currency: string;
  /** Fetched pay-currency options; `undefined` while loading. */
  payCurrencies: readonly PayCurrencyOption[] | undefined;
  /** `useEmployeeCompensation().schedule_change`; `undefined` when the write path is unreachable. */
  schedule:
    | ((
        input: ScheduleCompensationInput,
      ) => Promise<ScheduleCompensationResult>)
    | undefined;
  /** Called once the change is recorded, so the caller can refetch the profile + history. */
  onScheduled: () => void;
}

/**
 * Change salary dialog: schedules an effective-dated salary / rate / basis /
 * pay-currency change (`payroll.scheduleCompensationChange`). The date may be
 * in the past -- approved pay runs are never rewritten; the difference is
 * settled later as back pay, which the success notice explains. Pure logic
 * (draft, validation, wire mapping) lives in `change_salary_validation.ts`.
 */
export function ChangeSalaryDialog({
  open,
  onOpenChange,
  employee,
  currency,
  payCurrencies: fetchedPayCurrencies,
  schedule,
  onScheduled,
}: ChangeSalaryDialogProps): React.JSX.Element {
  const { t, locale } = useTranslation('wp');
  const datePickerLabels = useDatePickerLabels();
  const current = React.useMemo(() => currentFromRecord(employee), [employee]);
  const [draft, setDraft] = React.useState<ChangeSalaryDraft>(() =>
    initialChangeSalaryDraft(current, todayIsoUtc()),
  );
  const [errors, setErrors] = React.useState<ChangeSalaryErrors>({});
  const [submitting, setSubmitting] = React.useState(false);
  const [failure, setFailure] =
    React.useState<CompensationMutationFailure | null>(null);
  // Set once the change is recorded AND it reaches back into approved pay
  // runs: the dialog then stays open to explain the back-pay consequence.
  const [completed, setCompleted] = React.useState<{
    applied_now: boolean;
    effective_from: string;
    pay_runs: AffectedPayRun[];
  } | null>(null);

  const payCurrencies = React.useMemo(
    () =>
      withPayCurrencyFallbacks(
        fetchedPayCurrencies,
        currency,
        draft.pay_currency_code,
      ),
    [fetchedPayCurrencies, currency, draft.pay_currency_code],
  );

  // Start from the employee's current pay every time the dialog opens.
  React.useEffect(() => {
    if (!open) return;
    setDraft(initialChangeSalaryDraft(current, todayIsoUtc()));
    setErrors({});
    setFailure(null);
    setCompleted(null);
  }, [open, current]);

  function patch(next: Partial<ChangeSalaryDraft>): void {
    setDraft((d) => ({ ...d, ...next }));
  }

  function close(): void {
    // A backdated change with a back-pay notice defers the refetch until the
    // operator has read it (a refetch swaps the employee record underneath).
    if (completed !== null) onScheduled();
    onOpenChange(false);
  }

  async function handleSubmit(): Promise<void> {
    const found = validateChangeSalary(draft, current);
    setErrors(found);
    if (Object.keys(found).length > 0) return;
    if (schedule === undefined) {
      setFailure({ kind: 'error' });
      return;
    }

    setSubmitting(true);
    setFailure(null);
    const result = await schedule(buildScheduleInput(draft, current));
    setSubmitting(false);

    if (result.outcome === 'failure') {
      setFailure(result.failure);
      // A same-date conflict is resolvable: the next submit replaces the
      // existing entry (the button label says so).
      if (isSameDateConflict(result.failure))
        patch({ replace_same_date: true });
      return;
    }

    toast.success(
      result.applied_now
        ? t('employees.compensation_applied_today')
        : t('employees.compensation_scheduled_for', {
            date: format_date(draft.effective_from, locale),
          }),
    );
    if (result.back_pay_pay_runs.length > 0) {
      setCompleted({
        applied_now: result.applied_now,
        effective_from: draft.effective_from,
        pay_runs: result.back_pay_pay_runs,
      });
      return;
    }
    onOpenChange(false);
    onScheduled();
  }

  const isHourly = draft.pay_basis === 'hourly';
  const payCurrency = draft.pay_currency_code || currency;
  const reasonOptions = COMPENSATION_REASONS.map((reason) => ({
    value: reason,
    label: t(COMPENSATION_REASON_KEY[reason]),
  }));

  return (
    <AdaptiveDialog
      open={open}
      onClose={close}
      maxWidth="lg"
      title={t('employees.change_salary_title')}
      description={t('employees.change_salary_description')}
      footer={
        completed !== null ? (
          <Button variant="primary" onClick={close}>
            {t('common.close')}
          </Button>
        ) : (
          <>
            <Button variant="ghost" onClick={close}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="primary"
              loading={submitting}
              onClick={() => void handleSubmit()}
            >
              {draft.replace_same_date
                ? t('employees.compensation_replace_same_date')
                : t('employees.change_salary_submit')}
            </Button>
          </>
        )
      }
    >
      {completed !== null ? (
        <Alert
          variant="info"
          title={
            completed.applied_now
              ? t('employees.compensation_applied_today')
              : t('employees.compensation_scheduled_for', {
                  date: format_date(completed.effective_from, locale),
                })
          }
        >
          {t('employees.compensation_back_pay_notice', {
            periods: formatBackPayPeriods(completed.pay_runs, locale),
          })}
        </Alert>
      ) : (
        <div className="mt-4 grid grid-cols-1 gap-4">
          <FormField label={t('employees.form_pay_basis')}>
            <StyledSelect<CompensationPayBasis>
              value={draft.pay_basis}
              onChange={(pay_basis) => {
                patch({
                  pay_basis,
                  amount_cents: amountForBasis(current, pay_basis),
                });
              }}
              options={[
                {
                  value: 'hourly',
                  label: t('employees.form_pay_basis_hourly'),
                },
                {
                  value: 'salaried',
                  label: t('employees.form_pay_basis_salaried'),
                },
              ]}
              ariaLabel={t('employees.form_pay_basis')}
              triggerClassName={SELECT_TRIGGER_CLASSES}
            />
          </FormField>

          <EmployeePayAmountField
            id="change-salary-amount"
            label={
              isHourly
                ? t('employees.form_hourly_rate_currency', {
                    currency: payCurrency,
                  })
                : t('employees.form_monthly_salary', { currency: payCurrency })
            }
            error={errors.amount && t(AMOUNT_ERROR_KEY[errors.amount])}
            amountCents={draft.amount_cents}
            onAmountChange={(cents) => {
              patch({ amount_cents: cents });
            }}
            payCurrencyCode={draft.pay_currency_code}
            onPayCurrencyChange={(code) => {
              patch({ pay_currency_code: code });
            }}
            baseCurrency={currency}
            payCurrencies={payCurrencies}
            t={t}
          />
          {isCurrencyChanged(draft, current) && (
            <p className="nv-help">
              {t('employees.compensation_currency_boundary_hint')}
            </p>
          )}

          <FormField
            label={t('employees.compensation_effective_from')}
            htmlFor="change-salary-effective-from"
            error={
              errors.effective_from && t(DATE_ERROR_KEY[errors.effective_from])
            }
          >
            <DatePicker
              id="change-salary-effective-from"
              value={draft.effective_from || null}
              locale={locale}
              labels={datePickerLabels}
              aria-label={t('employees.compensation_effective_from')}
              error={Boolean(errors.effective_from)}
              {...(current.employment_start_date !== null
                ? { min: current.employment_start_date }
                : {})}
              {...(current.termination_date !== null
                ? { max: current.termination_date }
                : {})}
              onChange={(iso) => {
                // The replace-existing decision belongs to ONE date.
                patch({ effective_from: iso, replace_same_date: false });
              }}
            />
          </FormField>

          <FormField label={t('employees.compensation_reason')}>
            <StyledSelect<CompensationReason>
              value={draft.reason}
              onChange={(reason) => {
                patch({ reason });
              }}
              options={reasonOptions}
              ariaLabel={t('employees.compensation_reason')}
              triggerClassName={SELECT_TRIGGER_CLASSES}
            />
          </FormField>

          <FormField
            label={t('employees.compensation_note')}
            htmlFor="change-salary-note"
            error={errors.note && t('employees.form_error_too_long')}
          >
            <Textarea
              id="change-salary-note"
              rows={2}
              maxLength={NOTE_MAX_LENGTH}
              value={draft.note}
              error={Boolean(errors.note)}
              onChange={(e) => {
                patch({ note: e.target.value });
              }}
            />
          </FormField>

          {failure !== null && (
            <CompensationFailureAlert failure={failure} t={t} />
          )}
        </div>
      )}
    </AdaptiveDialog>
  );
}
